import { useCallback, useEffect, useRef, useState } from "react";

import { useWebRTC } from "providers/WebRTCProvider";
import { useGimbalControl } from "providers/GimbalControlProvider";
import { useAccessibilityPreferences } from "providers/AccessibilityPreferencesProvider";
import { useMap } from "providers/navigation/MapProvider";
import { StreamingState } from "lib/consts/streaming";
import { isFrontCameraStream } from "lib/utils/connection";
import { projectPlannedPathToVideoPixels } from "lib/navigation/projectPlannedPathToVideo";
import { drawDetectionsOnCanvas } from "lib/utils/drawDetectionsOnCanvas";
import { drawPlannedPathOnCanvas } from "lib/utils/drawPlannedPathOnCanvas";
import {
    canvasPixelToVideo,
    computeVideoObjectFitLayout,
} from "lib/utils/videoObjectFitLayout";

import Spinner from "components/layout/loading/Spinner";

/** Match bbox detection poll rate in WebRTCProvider (~15 fps). */
const OVERLAY_PAINT_MIN_MS = 1000 / 15;
const TOUCH_FOCUS_MARKER_MS = 700;

const StreamView = ({ className = "", touchFocusEnabled = true, streamName = null }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const detectionsRef = useRef(new Map());
    const plannedPathRef = useRef([]);
    const robotPoseRef = useRef(null);
    const overlayDirtyRef = useRef(true);
    const lastPaintAtRef = useRef(0);
    const lastCanvasSizeRef = useRef({ w: 0, h: 0 });
    const [streamStatus, setStreamStatus] = useState(StreamingState.None);
    const [focusMarker, setFocusMarker] = useState(null);
    const focusMarkerTimerRef = useRef(null);

    const {
        connectedToWebRTC,
        attachMediaStreamToElem,
        streamStatuses,
        viewerFailures,
        streamVersions,
        activeStream,
        fitScreen,
        streamDetections,
    } = useWebRTC();
    const { controlGimbal, sendGimbalTouchFocus } = useGimbalControl();
    const {
        bboxOverlayColor,
        streamPathOverlay,
        streamPathCameraConfig,
    } = useAccessibilityPreferences();
    const mapContext = useMap();
    const plannedPath = mapContext?.plannedPath;
    const robotPose = mapContext?.robotPose;

    // Optional override so pinned popups can show a non-active camera stream.
    const displayStream = streamName || activeStream;
    const videoReady = streamStatus === StreamingState.Okay || streamStatus === StreamingState.Slow;
    const viewerFailure = displayStream ? viewerFailures.get(displayStream) : null;

    const markOverlayDirty = useCallback(() => {
        overlayDirtyRef.current = true;
    }, []);

    // Keep refs so the paint loop sees current values without restarting rAF.
    const fitScreenRef = useRef(fitScreen);
    fitScreenRef.current = fitScreen;
    const bboxColorRef = useRef(bboxOverlayColor);
    bboxColorRef.current = bboxOverlayColor;
    const streamPathOverlayRef = useRef(streamPathOverlay);
    streamPathOverlayRef.current = streamPathOverlay;
    const streamPathCameraConfigRef = useRef(streamPathCameraConfig);
    streamPathCameraConfigRef.current = streamPathCameraConfig;

    useEffect(() => {
        detectionsRef.current = streamDetections;
        markOverlayDirty();
    }, [streamDetections, markOverlayDirty]);

    useEffect(() => {
        const hadPathOverlay =
            isFrontCameraStream(displayStream) && plannedPathRef.current.length >= 2;
        plannedPathRef.current = Array.isArray(plannedPath) ? plannedPath : [];
        const pose = robotPoseRef.current;
        const hasPathOverlay =
            isFrontCameraStream(displayStream) &&
            plannedPathRef.current.length >= 2 &&
            pose?.position &&
            pose?.orientation;
        if (hasPathOverlay || hadPathOverlay) markOverlayDirty();
    }, [plannedPath, displayStream, markOverlayDirty]);

    useEffect(() => {
        robotPoseRef.current = robotPose;
        const path = plannedPathRef.current;
        if (
            isFrontCameraStream(displayStream) &&
            path.length >= 2 &&
            robotPose?.position &&
            robotPose?.orientation
        ) {
            markOverlayDirty();
        }
    }, [robotPose, displayStream, markOverlayDirty]);

    useEffect(() => {
        markOverlayDirty();
    }, [fitScreen, bboxOverlayColor, streamPathOverlay, streamPathCameraConfig, markOverlayDirty]);

    useEffect(() => {
        markOverlayDirty();
        lastCanvasSizeRef.current = { w: 0, h: 0 };
    }, [displayStream, markOverlayDirty]);

    useEffect(() => () => {
        if (focusMarkerTimerRef.current != null) {
            clearTimeout(focusMarkerTimerRef.current);
        }
    }, []);

    useEffect(() => {
        return () => {
            if (focusMarkerTimerRef.current != null) {
                clearTimeout(focusMarkerTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (videoRef.current === null) return;

        const status = connectedToWebRTC && displayStream
            ? streamStatuses.get(displayStream)
            : undefined;
        setStreamStatus(status);
        attachMediaStreamToElem(
            videoRef.current,
            status && status !== StreamingState.None ? displayStream : null
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectedToWebRTC, displayStream, streamStatuses, streamVersions]);

    // Retry playback at media readiness boundaries for tracks that arrive before
    // the element has decoded its first frame.
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const resumePlayback = () => {
            if (!video.srcObject || !video.paused) return;
            const promise = video.play();
            if (promise?.catch) promise.catch(() => undefined);
        };

        video.addEventListener("loadedmetadata", resumePlayback);
        video.addEventListener("canplay", resumePlayback);
        resumePlayback();
        return () => {
            video.removeEventListener("loadedmetadata", resumePlayback);
            video.removeEventListener("canplay", resumePlayback);
        };
    }, [connectedToWebRTC, displayStream, streamVersions]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!canvas || !video) return;

        let raf = 0;
        const paintOverlay = () => {
            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            const cw = video.clientWidth;
            const ch = video.clientHeight;
            const sizeChanged =
                cw !== lastCanvasSizeRef.current.w || ch !== lastCanvasSizeRef.current.h;
            if (sizeChanged) lastCanvasSizeRef.current = { w: cw, h: ch };
            if (canvas.width !== cw || canvas.height !== ch) {
                canvas.width = cw;
                canvas.height = ch;
            }

            const vw = video.videoWidth;
            const vh = video.videoHeight;
            ctx.clearRect(0, 0, cw, ch);

            const det = detectionsRef.current.get(displayStream);
            drawDetectionsOnCanvas(ctx, cw, ch, vw, vh, det, {
                fitScreen: fitScreenRef.current,
                bboxColor: bboxColorRef.current,
                clearCanvas: false,
            });

            const path = plannedPathRef.current;
            const pose = robotPoseRef.current;
            if (
                isFrontCameraStream(displayStream) &&
                path.length >= 2 &&
                pose?.position &&
                pose?.orientation &&
                vw &&
                vh
            ) {
                const projected = projectPlannedPathToVideoPixels(
                    path,
                    pose,
                    vw,
                    vh,
                    streamPathCameraConfigRef.current,
                );
                const pathPrefs = streamPathOverlayRef.current;
                drawPlannedPathOnCanvas(ctx, cw, ch, vw, vh, projected, {
                    fitScreen: fitScreenRef.current,
                    color: pathPrefs.pathColor,
                    lineWidth: pathPrefs.lineWidth,
                    opacity: pathPrefs.opacity,
                });
            }

            overlayDirtyRef.current = false;
            lastPaintAtRef.current = performance.now();
        };

        const loop = () => {
            raf = requestAnimationFrame(loop);
            const cw = video.clientWidth;
            const ch = video.clientHeight;
            const sizeChanged =
                cw !== lastCanvasSizeRef.current.w || ch !== lastCanvasSizeRef.current.h;
            if (!overlayDirtyRef.current && !sizeChanged) return;

            const now = performance.now();
            if (
                overlayDirtyRef.current &&
                !sizeChanged &&
                now - lastPaintAtRef.current < OVERLAY_PAINT_MIN_MS
            ) return;

            paintOverlay();
        };

        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [displayStream]);

    const handleTouchFocus = useCallback(
        (event) => {
            if (!touchFocusEnabled || !controlGimbal) return;

            const video = videoRef.current;
            if (!video?.videoWidth || !video?.videoHeight) return;

            const rect = video.getBoundingClientRect();
            const canvasX = event.clientX - rect.left;
            const canvasY = event.clientY - rect.top;
            const layout = computeVideoObjectFitLayout(
                rect.width,
                rect.height,
                video.videoWidth,
                video.videoHeight,
                fitScreen,
            );
            if (!layout) return;

            const pixel = canvasPixelToVideo(
                canvasX,
                canvasY,
                layout,
                video.videoWidth,
                video.videoHeight,
            );
            if (!pixel) return;

            sendGimbalTouchFocus({ x: pixel.x, y: pixel.y });
            setFocusMarker({ x: canvasX, y: canvasY });
            if (focusMarkerTimerRef.current != null) {
                clearTimeout(focusMarkerTimerRef.current);
            }
            focusMarkerTimerRef.current = setTimeout(() => {
                setFocusMarker(null);
                focusMarkerTimerRef.current = null;
            }, TOUCH_FOCUS_MARKER_MS);
        },
        [touchFocusEnabled, controlGimbal, fitScreen, sendGimbalTouchFocus],
    );

    return (
        <div className={`relative ${className}`}>
            {!videoReady && (
                viewerFailure ? (
                    <div role="alert" className="absolute inset-0 flex items-center justify-center bg-black/70 p-4 text-center text-sm text-white">
                        {viewerFailure}
                    </div>
                ) : (
                    <Spinner className="absolute h-2/4" />
                )
            )}
            <video
                ref={videoRef}
                muted
                playsInline
                autoPlay
                onLoadedMetadata={(event) => console.info("[video] metadata", { cameraKey: displayStream, width: event.currentTarget.videoWidth, height: event.currentTarget.videoHeight })}
                onPlaying={() => console.info("[video] playing", { cameraKey: displayStream })}
                onWaiting={() => console.info("[video] waiting", { cameraKey: displayStream })}
                onStalled={() => console.warn("[video] stalled", { cameraKey: displayStream })}
                onError={(event) => console.warn("[video] error", { cameraKey: displayStream, code: event.currentTarget.error?.code || null })}
                className={`h-full w-full ${fitScreen && "object-cover"} ${!videoReady && "invisible"}`}
            />
            <canvas
                ref={canvasRef}
                aria-hidden
                className={`pointer-events-none absolute inset-0 h-full w-full ${fitScreen && "object-cover"} ${!videoReady && "invisible"}`}
            />
            {touchFocusEnabled && controlGimbal && (
                <button
                    type="button"
                    aria-label="Touch to focus"
                    className="absolute inset-0 z-[1] cursor-crosshair border-0 bg-transparent p-0"
                    onClick={handleTouchFocus}
                />
            )}
            {focusMarker && (
                <div
                    aria-hidden
                    className="pointer-events-none absolute z-[2] h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.45)]"
                    style={{ left: focusMarker.x, top: focusMarker.y }}
                />
            )}
        </div>
    );
};

export default StreamView;
